<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, email } from '@vuelidate/validators';
export default {
  components: {
    NextButton,
  },
  props: {
    name: {
      type: String,
      default: '',
    },
    email: {
      type: String,
      default: '',
    },
    displayName: {
      type: String,
      default: '',
    },
    emailEnabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['updateUser'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      userName: this.name,
      userDisplayName: this.displayName,
      userEmail: this.email,
      inputStyles: {
        borderRadius: '0.75rem',
        padding: '0.375rem 0.75rem',
        fontSize: '0.875rem',
        marginBottom: '0.125rem',
      },
    };
  },
  computed: {
    ...mapGetters({
      getCurrentRole: 'getCurrentRole',
    }),
    isAdmin() {
      return this.getCurrentRole === 'administrator';
    },
    disabledInputStyles() {
      return {
        ...this.inputStyles,
        backgroundColor: '#f5f5f5',
        color: '#6b7280',
        cursor: 'not-allowed',
        borderColor: '#d1d5db',
      };
    },
  },
  validations: {
    userName: {
      required,
      minLength: minLength(1),
    },
    userDisplayName: {},
    userEmail: {
      required,
      email,
    },
  },
  watch: {
    name: {
      handler(value) {
        this.userName = value;
      },
      immediate: true,
    },
    displayName: {
      handler(value) {
        this.userDisplayName = value;
      },
      immediate: true,
    },
    email: {
      handler(value) {
        this.userEmail = value;
      },
      immediate: true,
    },
  },
  methods: {
    async updateUser() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        useAlert(this.$t('PROFILE_SETTINGS.FORM.ERROR'));
        return;
      }

      // Apenas administradores podem alterar nome e displayName
      const userUpdates = {
        email: this.userEmail,
      };

      if (this.isAdmin) {
        userUpdates.name = this.userName;
        userUpdates.displayName = this.userDisplayName;
      }

      this.$emit('updateUser', userUpdates);
    },
  },
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="updateUser('profile')">
    <woot-input
      v-model="userName"
      :styles="isAdmin ? inputStyles : disabledInputStyles"
      :class="{
        error: v$.userName.$error,
        'input-disabled': !isAdmin
      }"
      :label="$t('PROFILE_SETTINGS.FORM.NAME.LABEL')"
      :placeholder="$t('PROFILE_SETTINGS.FORM.NAME.PLACEHOLDER')"
      :error="`${
        v$.userName.$error ? $t('PROFILE_SETTINGS.FORM.NAME.ERROR') : ''
      }`"
      :help-text="!isAdmin ? $t('PROFILE_SETTINGS.FORM.NAME.ADMIN_ONLY_NOTE') : ''"
      :readonly="!isAdmin"
      @input="v$.userName.$touch"
      @blur="v$.userName.$touch"
    />
    <woot-input
      v-model="userDisplayName"
      :styles="isAdmin ? inputStyles : disabledInputStyles"
      :class="{
        error: v$.userDisplayName.$error,
        'input-disabled': !isAdmin
      }"
      :label="$t('PROFILE_SETTINGS.FORM.DISPLAY_NAME.LABEL')"
      :placeholder="$t('PROFILE_SETTINGS.FORM.DISPLAY_NAME.PLACEHOLDER')"
      :error="`${
        v$.userDisplayName.$error
          ? $t('PROFILE_SETTINGS.FORM.DISPLAY_NAME.ERROR')
          : ''
      }`"
      :help-text="!isAdmin ? $t('PROFILE_SETTINGS.FORM.DISPLAY_NAME.ADMIN_ONLY_NOTE') : ''"
      :readonly="!isAdmin"
      @input="v$.userDisplayName.$touch"
      @blur="v$.userDisplayName.$touch"
    />
    <woot-input
      v-if="emailEnabled"
      v-model="userEmail"
      :styles="inputStyles"
      :class="{ error: v$.userEmail.$error }"
      :label="$t('PROFILE_SETTINGS.FORM.EMAIL.LABEL')"
      :placeholder="$t('PROFILE_SETTINGS.FORM.EMAIL.PLACEHOLDER')"
      :error="`${
        v$.userEmail.$error ? $t('PROFILE_SETTINGS.FORM.EMAIL.ERROR') : ''
      }`"
      @input="v$.userEmail.$touch"
      @blur="v$.userEmail.$touch"
    />
    <div>
      <NextButton
        type="submit"
        :label="$t('PROFILE_SETTINGS.BTN_TEXT')"
        :disabled="!isAdmin && !emailEnabled"
      />
    </div>
  </form>
</template>

<style scoped>
.input-disabled {
  opacity: 0.7;
  position: relative;
}

.input-disabled input {
  background-color: #f9fafb !important;
  color: #6b7280 !important;
  cursor: not-allowed !important;
  border-color: #d1d5db !important;
  background-image: linear-gradient(45deg, transparent 40%, rgba(209, 213, 219, 0.1) 50%, transparent 60%);
}

.input-disabled input:focus {
  border-color: #d1d5db !important;
  box-shadow: none !important;
}

.input-disabled label {
  color: #6b7280 !important;
  position: relative;
}

.input-disabled label::after {
  content: " 🔒";
  font-size: 12px;
  opacity: 0.8;
  margin-left: 4px;
}

/* Estilo para text de ajuda quando campo está bloqueado */
.input-disabled .help-text {
  color: #ef4444 !important;
  font-weight: 500;
}
</style>
